<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { revokeContributors } from './api'
import GbButton from '@/components/GbButton.vue'
import { useUserStore } from '@/authentication/user.store'
import { ToastProvider, ToastRoot, ToastViewport } from 'reka-ui'
import { storeToRefs } from 'pinia'
import { useLibraryStore } from './library.store'
import { useConfirm } from 'primevue/useconfirm'
import ConfirmDialog from 'primevue/confirmdialog'
import { useRouter } from 'vue-router'
import { useLibraryFolderAnalytics } from '@/library/composables/useLibraryFolderAnalytics.ts'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { EVENT_NAMES } from '@/tracking/analytics/event_names.ts'

const userStore = useUserStore()
const userId = computed(() => userStore.userId)
const libraryStore = useLibraryStore()
const { currentFolder } = storeToRefs(libraryStore)
const confirm = useConfirm()
const router = useRouter()

const props = defineProps<{
  externalOpen: boolean
  userUuid: string
  userName?: string
}>()

watch(() => props.externalOpen, (newValue) => {
  if (!newValue) {
    confirm.close()
  } else {
    removeContributor()
  }
})
const emit = defineEmits<{
  (e: 'update:externalOpen', value: boolean): void
  (e: 'modal-closed'): void
}>()

const showToast = ref(false)
const toastMessage = ref('')
const toastError = ref(false)
const toastTimeout = 5000

const showToastHandler = (message: string, error: boolean) => {
  toastMessage.value = message
  toastError.value = error
  showToast.value = true
  setTimeout(() => {
    requestAnimationFrame(() => {
      showToast.value = false
    })
  }, toastTimeout)
}

const { getReactiveEventAttributes } = useLibraryFolderAnalytics()
const analytics = getAnalyticsTracker().getChildContext(getReactiveEventAttributes())

const removeContributor = async function() {
  if (!currentFolder.value?.uuid || !props.userUuid) {
    emit('update:externalOpen', false)
    emit('modal-closed')
    return
  }
  confirm.require({
    group: 'remove-contributor-folder',
    header: props.userName || '',
    message: `${props.userName || 'You'} will no longer be able to contribute to this folder.`,
    reject: () => {
      emit('update:externalOpen', false)
      emit('modal-closed')
    },
    accept: async () => {
      try {
        await revokeContributors(currentFolder.value.uuid, [props.userUuid])
        analytics.sendEvent(EVENT_NAMES.LIBRARY.FOLDER_ACTION.REMOVED_FOLDER_USER, {
          removed_user_uuid: props.userUuid,
        })
        emit('update:externalOpen', false)
        emit('modal-closed')
        showToastHandler('Share settings updated', false)
        // user is removing themselves. redirect to all folders if in a library folder
        if (userId.value === props.userUuid) {
          if (router.currentRoute.value.name === 'LibraryFolder') {
            router.push({
              name: 'AllFolders'
            })
          }
        }
      } catch (e) {
        console.error(e)
        emit('update:externalOpen', false)
        emit('modal-closed')
        showToastHandler('There was an error removing contributors. Please try again later.', true)
      } finally {
        await libraryStore.fetchFolders(userId.value)
     }
    }
  })
}
</script>

<template>
  <ConfirmDialog group="remove-contributor-folder" class="w-auto md:w-[38rem]">
    <template #container="{ message, acceptCallback, rejectCallback }">
      <div class="flex flex-col rounded-md bg-surface-50">
        <div class="mt-2 flex justify-between border-b border-gray-300 p-3">
          <span class="block pl-3 text-xl font-medium fs-exclude">
            {{ `Remove ${message.header || 'yourself'} as a Contributor?` }}
          </span>
          <GbButton
            size="small"
            rounded
            :icon-only="true"
            aria-label="close dialog"
            gbTheme="grayTertiary"
            class="mr-4 self-start"
            @click="rejectCallback"
          >
            <span class="material-icons-round">close</span>
          </GbButton>
        </div>
        <p class="py-5 pl-7 pr-12 text-gray-700 fs-exclude">{{ message.message }}</p>
        <div class="flex items-center justify-end gap-2 border-t border-gray-300 p-4">
          <GbButton aria-label="Cancel" label="Cancel" @click="rejectCallback" gbTheme="grayOutlineSecondary"></GbButton>
          <GbButton aria-label="Remove Contributor" label="Remove Contributor" @click="acceptCallback" gbTheme="redPrimary"></GbButton>
        </div>
      </div>
    </template>
  </ConfirmDialog>
    <ToastProvider>
      <ToastRoot
        :open="showToast"
        @update:open="showToast = $event"
        class="rounded-md shadow-sm py-2 px-4 items-center data-[state=open]:animate-slideIn data-[state=closed]:animate-hide data-[swipe=cancel]:translate-x-0 data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=end]:animate-swipeOut data-[swipe=cancel]:transition-[transform_200ms_ease-out]"
        :class="toastError ? 'bg-red-100 text-default border-red-300 border-1' : 'bg-black text-white'"
      >
        <div class="flex items-center">
          <span class="material-icons-outlined text-display-sm" v-if="!toastError">group</span>
          <span class="material-icons-round text-display-sm text-red-300" v-else>warning_amber</span>
          <span class="ml-2">{{ toastMessage }}</span>
        </div>
      </ToastRoot>
      <ToastViewport v-if="showToast" :label="`Notification - remove contributors`" class="fixed top-0 left-0 right-0 flex justify-center pt-[70px] px-[25px] gap-[10px] w-full max-w-[100vw] m-0 list-none z-[999999] outline-none" />
    </ToastProvider>
</template>
